import { useState, useEffect } from 'react';
import { collection, getDocs, query, where, limit } from 'firebase/firestore';
import { db } from '../../firebase/config';

const CAUSES = [
  { value: 'dummy',         label: 'Dummy',   icon: '🍭' },
  { value: 'bottle',        label: 'Bottle',  icon: '🍼' },
  { value: 'breastfeeding', label: 'Nursing', icon: '🤱' },
  { value: 'nappy',         label: 'Nappy',   icon: '🧷' },
  { value: 'noise',         label: 'Noise',   icon: '🔊' },
  { value: 'unknown',       label: 'Unknown', icon: '❓' },
  { value: 'other',         label: 'Other',   icon: '📝' },
];

/** Map of cause value → display label (shared with table + edit modal). */
export const CAUSE_LABELS = Object.fromEntries(CAUSES.map(c => [c.value, c.label]));

/**
 * SleepInterruptionModal — quick-log modal for wake-ups during a live sleep timer.
 *
 * Props:
 *   isOpen    — boolean
 *   onClose   — () => void
 *   onConfirm — (wakeUpEntry) => void  called with the completed wake-up object
 *   childId   — string  used to fetch average bottle amount
 */
export default function SleepInterruptionModal({ isOpen, onClose, onConfirm, childId }) {
  const [cause, setCause]               = useState('dummy');
  const [wentBackToSleep, setBack]      = useState(true);
  const [logFeed, setLogFeed]           = useState(false);
  const [bottleAmount, setBottleAmount] = useState('');
  const [bfDuration, setBfDuration]     = useState(10);
  const [notes, setNotes]               = useState('');
  const [showNotes, setShowNotes]       = useState(false);
  const [avgBottle, setAvgBottle]       = useState(null);
  const [fetchedAvg, setFetchedAvg]     = useState(false);

  // Fetch average bottle amount the first time "bottle" is selected in this session
  useEffect(() => {
    if (cause !== 'bottle' || !childId || fetchedAvg) return;
    setFetchedAvg(true);
    (async () => {
      try {
        const snap = await getDocs(query(
          collection(db, 'bottleFeeds'),
          where('childId', '==', childId),
          limit(20),
        ));
        const amounts = snap.docs
          .map(d => parseFloat(d.data().amount))
          .filter(n => !isNaN(n) && n > 0);
        if (!amounts.length) return;
        const avg     = amounts.reduce((s, n) => s + n, 0) / amounts.length;
        const rounded = Math.round(avg * 2) / 2; // nearest 0.5 oz
        setAvgBottle(rounded);
        setBottleAmount(String(rounded));
      } catch { /* non-fatal */ }
    })();
  }, [cause, childId, fetchedAvg]);

  // Clear "also log" toggle when switching to a non-feed cause
  useEffect(() => {
    if (cause !== 'bottle' && cause !== 'breastfeeding') setLogFeed(false);
  }, [cause]);

  const reset = () => {
    setCause('dummy');
    setBack(true);
    setLogFeed(false);
    setBottleAmount(avgBottle ? String(avgBottle) : '');
    setBfDuration(10);
    setNotes('');
    setShowNotes(false);
    setFetchedAvg(false); // allow re-fetch next open
  };

  const handleConfirm = () => {
    const entry = {
      time:            new Date().toISOString(),
      cause,
      wentBackToSleep,
      notes:           notes.trim(),
    };
    if (logFeed && cause === 'bottle') {
      entry.feedLogged   = true;
      entry.bottleAmount = parseFloat(bottleAmount) || null;
    } else if (logFeed && cause === 'breastfeeding') {
      entry.feedLogged  = true;
      entry.bfDuration  = parseInt(bfDuration, 10) || 10;
    }
    onConfirm(entry);
    reset();
    onClose();
  };

  if (!isOpen) return null;

  const isFeedCause = cause === 'bottle' || cause === 'breastfeeding';

  return (
    <div className="interruption-backdrop" onClick={onClose}>
      <div className="interruption-modal" onClick={e => e.stopPropagation()}>

        <div className="interruption-header">
          <span className="interruption-title">Log wake-up</span>
          <button className="interruption-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {/* ── Cause ───────────────────────────────────────────── */}
        <p className="interruption-label">What woke them?</p>
        <div className="interruption-causes">
          {CAUSES.map(c => (
            <button
              key={c.value}
              className={`interruption-cause-btn${cause === c.value ? ' active' : ''}`}
              onClick={() => setCause(c.value)}
            >
              <span className="interruption-cause-icon">{c.icon}</span>
              <span className="interruption-cause-text">{c.label}</span>
            </button>
          ))}
        </div>

        {/* ── Back to sleep ────────────────────────────────────── */}
        <div className="interruption-toggle-row">
          <span className="interruption-label" style={{ margin: 0 }}>Back to sleep?</span>
          <div className="interruption-toggle">
            <button
              className={`interruption-toggle-btn${wentBackToSleep ? ' active' : ''}`}
              onClick={() => setBack(true)}
            >Yes</button>
            <button
              className={`interruption-toggle-btn${!wentBackToSleep ? ' active' : ''}`}
              onClick={() => setBack(false)}
            >No</button>
          </div>
        </div>

        {/* ── Feed auto-log ─────────────────────────────────────── */}
        {isFeedCause && (
          <div className="interruption-feed-section">
            <label className="interruption-feed-label">
              <input
                type="checkbox"
                checked={logFeed}
                onChange={e => setLogFeed(e.target.checked)}
              />
              Also log this {cause === 'bottle' ? 'bottle feed' : 'nursing session'}?
            </label>

            {logFeed && cause === 'bottle' && (
              <div className="interruption-feed-input">
                <span className="interruption-label" style={{ margin: 0 }}>Amount (oz)</span>
                <input
                  type="number"
                  className="form-control form-control-sm"
                  step="0.5"
                  min="0"
                  value={bottleAmount}
                  onChange={e => setBottleAmount(e.target.value)}
                  style={{ width: 90 }}
                />
                {avgBottle && (
                  <span className="interruption-avg-hint">avg: {avgBottle} oz</span>
                )}
              </div>
            )}

            {logFeed && cause === 'breastfeeding' && (
              <div className="interruption-feed-input">
                <span className="interruption-label" style={{ margin: 0 }}>Duration (min)</span>
                <input
                  type="number"
                  className="form-control form-control-sm"
                  min="1"
                  value={bfDuration}
                  onChange={e => setBfDuration(e.target.value)}
                  style={{ width: 80 }}
                />
              </div>
            )}
          </div>
        )}

        {/* ── Notes (collapsible) ───────────────────────────────── */}
        {showNotes ? (
          <textarea
            className="form-control form-control-sm interruption-notes"
            placeholder="Optional note…"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            rows={2}
          />
        ) : (
          <button className="interruption-notes-toggle" onClick={() => setShowNotes(true)}>
            + Add note
          </button>
        )}

        <div className="interruption-actions">
          <button className="btn btn-sm btn-outline-secondary" onClick={() => { reset(); onClose(); }}>
            Cancel
          </button>
          <button className="btn btn-sm btn-primary" onClick={handleConfirm}>
            Log wake-up
          </button>
        </div>
      </div>
    </div>
  );
}
